<template>
  <div class="accordion-element" :id="id">
    <div
      class="accordion-element__trigger"
      @click="$_negateStatus(opened)"
      @mouseover="updateMouseCursor('button')"
      @mouseout="updateMouseCursor('default')">
      <div
        class="accordion-element__title"><h3 v-html="title"></h3></div>
      <div
        class="accordion-item__action-button"
        :class="classList"></div>
    </div>
    <div
      class="accordion-element__content"
      :class="{ 'accordion-element__content--open': opened }"
      @transitionend="$_castEnd(opened)">
      <div class="accordion-element__content__inner">
        <div v-html="content"></div>
        <div v-if="button">
          <rossetto-button :type="'pdf'" :url="button.url">
            {{button.text}}
          </rossetto-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapMutations } from 'vuex'
import RossettoButton from '@/components/RossettoButton'

export default {
  components: {
    RossettoButton
  },
  data: () => ({
    openAnimationEnd: undefined,
    closeAnimationEnd: undefined
  }),
  props: {
    title: {
      type: String,
      required: true
    },
    id: {
      type: String,
      default: () => ''
    },
    content: {
      type: String,
      required: true
    },
    button: {
      type: Object,
      required: false
    },
    opened: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    classList: {
      get () {
        return {
          'accordion-item__action-button--open': this.opened,
          'accordion-item__action-button--close': !this.opened
        }
      }
    }
  },
  mounted () {
    this.$_scrollIfHashed(this.$route.hash)
  },
  methods: {
    ...mapMutations('animations', [
      'updateMouseCursor'
    ]),
    open () {
      this.$emit('open')
      return new Promise((resolve, reject) => { this.openAnimationEnd = resolve })
    },
    close () {
      const animationEnd = new Promise((resolve, reject) => { this.closeAnimationEnd = resolve })
      if (!this.opened) this.closeAnimationEnd()
      this.$emit('close')
      return animationEnd
    },
    $_scrollIfHashed (routeHash) {
      const cleanHash = _.trimStart(routeHash, '#')
      if (cleanHash.length > 0 && cleanHash === this.id) window.setTimeout(this.open, 500)
    },
    $_castEnd (status) {
      if (status) this.openAnimationEnd()
      else this.closeAnimationEnd()
    },
    $_negateStatus (status) {
      if (!status) this.open()
      else this.close()
    }
  }
}
</script>

<style lang="scss">
@import '../../assets/scss/breakpoint.scss';
@import '../../assets/scss/mixins.scss';

$header_height: 50px;
$textAccWhite: #F2F2F2;
$textAccHighlight: #F7003F;
$buttonSize: 40px;

.accordion-element__content {
  max-height: 0;
  overflow: hidden;
  transition: max-height .85s ease-out;
}

.accordion-element__content--open {
  max-height: calc(300vh + #{$header_height})
}

/* @group Layout */

.accordion-element:not(:last-child){
  border-bottom: 1px solid rgba(255, 255, 255, .1);
}
.accordion-element > *{
  margin: 0;
  padding-left: 7.5%;
  padding-right: 7.5%;
  @include media(">=large"){
    padding-left: 15%;
    padding-right: 15%;
  }
}
.accordion-element__trigger{
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding-top: 2rem;
  padding-bottom: 2rem;
  cursor: pointer;
}
.accordion-element__title{
  width: calc(100% - #{$buttonSize});
  font-weight: bold;
}
.accordion-element__content__inner{
  padding: 0 0 5rem;
  width: 100%;

  ul li {
    display: block !important;
    margin: 2rem 0;
  }
}
.accordion-element{
  p + ul{
    margin-top: 5rem;
  }
  h4{
    color: $textAccWhite;
    margin-bottom: 3rem;
    padding-top: 5rem;
    font-size: 1.6rem;
    text-transform: uppercase;
    font-weight: 600;
  }

  a {
    color: #0487a3;
    text-decoration: none;
    position: relative;

    &::after{
      display: block;
      content: "";
      position: absolute;
      bottom: 0;
      width: 100%;
      height: 1px;
      background: #0487a3;;
      transform: scaleX(0);
      transform-origin: right;
      transition: transform .3s ease-out;
    }
    &:hover{
      &::after{
        transform: scaleX(1);
        transform-origin: left;
      }
    }
  }
}

.haslogo .accordion-element{
  .accordion-element__content__inner{
    @include media(">=medium"){
      & > div{
        display: flex;
        align-items: center;
      }
    }
  }
  img{
    width: 70px;
    height: auto;
    margin-bottom: 2rem;
  }
  @include media(">=medium"){
    img{
      width: 110px;
      height: auto;
      margin-bottom: 0;
    }
    img + p{
      margin-left: 40px;
    }
  }
}
/* @end */

/* @group Typo */

.accordion-element{

  img {

    object-fit: contain;

    @include media(">=large"){
        width: auto;
    }

  }

  h3{
    margin: 0;
    font-size: 2.1rem;
    line-height: 1.5;
    max-width: 850px;
  }
  p{
    margin: 0;
    font-size: 1.6rem;
    line-height: 1.7;
  }

  li{
    font-size: 1.7rem;
    line-height: 2;
  }

  ul{
    @include slashedList();
    li{

      @include media(">=large"){
        margin-bottom: 0;
        span{
          width: auto;
        }
      }

      &:before{
        content: "•";
        width: auto;
        background: transparent;
        transform: translateY(0rem);

        @include media(">=large"){
          margin-right: 1rem;
        }
      }
    }
  }

  @include media(">=large"){

    h3{
      font-size: 1.8rem;
      font-weight: 600;
    }
    p{
      font-size: 1.8rem;
    }
    li{
      font-size: 1.8rem;
      width: auto;
    }

  }

  @include media(">=xlarge"){

    h3{
      font-size: 3.6rem;
    }
    p{
      font-size: 2.4rem;
    }
    li{
      font-size: 2.4rem;
    }

  }

}

/* @end */

/* @group Open/Close ico */

.accordion-item__action-button{
  width: 25px;
  height: 25px;
  position: relative;
  box-sizing: border-box;
  line-height: 25px;
  display: inline-block;

  &:before, &:after{
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    margin-left: calc(-25px / 2);
    display: block;
    height: 2px;
    width: 25px;
    background-color: #0b3277;
  }
  &:after{
    transform: rotate(90deg) scaleX(1);
    transition: transform .25s ease-out;
  }
  &--open{
    &:after{
      transform: rotate(90deg) scaleX(0);
    }
  }

  @include media(">=xlarge"){
    width: 40px;
    height: 40px;
    line-height: 40px;

    &:before, &:after{
      margin-left: calc(-40px / 2);
      width: 40px;
    }
  }

}

/* @end */

</style>
